import { withRequestDeadline } from "@softmax/observatory-client/request-signal";

import { apiGet } from "@softmax/observatory-client/client";
import { ApiError } from "@softmax/observatory-client/transport";
import type {
  LeaguePublic,
  LeagueSummary,
} from "@softmax/observatory-client/types";
import {
  namesLeagueExactly,
  theaterCorpusFor,
  watchAddresses,
  watchableLeagues,
} from "@softmax/web-features/leagues";

import { getActualViewer } from "@/data/viewer";
import { readGameLeague, type GamePageContext } from "@/features/game/data";
import { watchLeagues } from "@/data/catalog";
import { publicRead } from "@softmax/web-features/data/public-api";

async function viewerWatchLeagues({
  signal,
  gameId,
}: {
  signal?: AbortSignal | undefined;
  gameId: string | undefined;
}) {
  const viewer = await getActualViewer({ signal });

  if (!viewer) return null;

  const query = gameId ? `?${new URLSearchParams({ game_id: gameId })}` : "";
  const leagues = await apiGet<LeagueSummary[]>(`/v2/leagues${query}`, {
    elevatedPrivileges: false,
    signal: withRequestDeadline(signal, 8000),
  });

  return { leagues: watchableLeagues(leagues), viewer };
}

export async function theaterCorpus({
  signal,
  slug,
  context,
}: {
  signal?: AbortSignal | undefined;
  slug: string;
  context: GamePageContext | null;
}) {
  const withCurrentAddress = (corpus: LeagueSummary[] | null) =>
    corpus?.map((league) => {
      if (!context?.gameId || league.game.id !== context.gameId) return league;

      const game = { ...league.game, game_address: context.address };

      if (context.league?.requestedSlug === null)
        game.default_league_id = context.league.id;

      return { ...league, game };
    }) ?? null;

  const publicLeagues = context?.gameId
    ? await publicRead<LeagueSummary[]>({
        path: `/v2/leagues?${new URLSearchParams({ game_id: context.gameId })}`,
        signal,
      })
        .then(watchableLeagues)
        .catch((error: unknown) => {
          if (
            error instanceof ApiError &&
            [401, 403, 404].includes(error.status)
          )
            return [];

          throw error;
        })
    : await watchLeagues({ signal });
  const publicCorpus = withCurrentAddress(publicLeagues);
  const [, ...league] = slug.split("/");
  const address = context?.gameId
    ? [context.address.slug, ...league].join("/")
    : slug;
  const publicHit =
    publicCorpus !== null &&
    namesLeagueExactly({
      addresses: watchAddresses(publicCorpus),
      slug: address,
    });
  const privateCorpus = publicHit
    ? null
    : await viewerWatchLeagues({
        signal,
        gameId: context?.gameId ?? undefined,
      });
  const result = theaterCorpusFor({
    slug: address,
    publicCorpus,
    viewerCorpus: withCurrentAddress(privateCorpus?.leagues ?? null),
  });

  if (
    context?.gameId &&
    !result.leagues?.some((entry) => entry.game.id === context.gameId)
  ) {
    return { leagues: [], viewerOnly: true, viewer: null };
  }

  return {
    ...result,
    viewer: result.viewerOnly ? (privateCorpus?.viewer ?? null) : null,
  };
}

export async function theaterLeague({
  signal,
  id,
  isPublic,
}: {
  signal?: AbortSignal | undefined;
  id: string;
  isPublic: boolean;
}): Promise<LeaguePublic> {
  const result = await readGameLeague({ signal, id, isPublic });

  if (result.status === "ok") return result.league;

  throw new Error(`League ${id} is ${result.status}`);
}
